import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { toast } from 'sonner';

import { apiClient, type AdminUserDetail } from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

import { durationToWire, formatCountdown, quotaPct, type ExpiryUnit } from '@/lib/tempAccount';
import { formatTokenCount } from '@/lib/format';

function parseLimit(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n > 0 ? n : NaN;
}

function QuotaBar({
  label,
  used,
  limit
}: {
  label: string;
  used?: number;
  limit?: number | null;
}) {
  const pct = quotaPct(used, limit);
  if (pct == null) return null;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted-foreground font-mono tabular-nums">
        <span>{label}</span>
        <span>
          {formatTokenCount(used)} / {formatTokenCount(limit)}
        </span>
      </div>
      <Progress value={pct} className="h-2" />
    </div>
  );
}

export default function TempAccountCard({
  detail,
  onUpdated
}: {
  detail: AdminUserDetail;
  onUpdated: (next: AdminUserDetail) => void;
}) {
  const [expireValue, setExpireValue] = useState('');
  const [expireUnit, setExpireUnit] = useState<ExpiryUnit>('days');
  const [inputTokens, setInputTokens] = useState('');
  const [outputTokens, setOutputTokens] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setExpireValue(detail.temp_day_limit != null ? String(detail.temp_day_limit) : '');
    setExpireUnit('days');
    setInputTokens(detail.temp_input_token_limit != null ? String(detail.temp_input_token_limit) : '');
    setOutputTokens(detail.temp_output_token_limit != null ? String(detail.temp_output_token_limit) : '');
  }, [
    detail.identifier,
    detail.temp_day_limit,
    detail.temp_input_token_limit,
    detail.temp_output_token_limit
  ]);

  const save = async (temporary: boolean) => {
    let body;
    if (temporary) {
      const inputLimit = parseLimit(inputTokens);
      const outputLimit = parseLimit(outputTokens);
      const duration = durationToWire(expireValue, expireUnit);
      if (duration == null || Number.isNaN(inputLimit) || Number.isNaN(outputLimit)) {
        toast.error('Limits must be whole numbers greater than zero, or left blank.');
        return;
      }
      body = {
        temporary: true,
        day_limit: duration.day_limit,
        expire_in_minutes: duration.expire_in_minutes,
        input_token_limit: inputLimit,
        output_token_limit: outputLimit
      };
    } else {
      body = { temporary: false };
    }
    setBusy(true);
    try {
      const updated = await apiClient.setAdminUserTemp(detail.identifier, body);
      onUpdated(updated);
      toast.success(temporary ? 'Temporary limits saved' : 'Converted to permanent account');
    } catch (error: any) {
      console.error('Temp update failed', error);
      toast.error(error?.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-card border-2">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-sm">
          <span>Temporary account</span>
          {detail.is_temporary ? (
            <Badge variant="secondary" className="gap-1">
              <Clock className="h-3 w-3" />
              {formatCountdown(detail.temp_expires_at)}
            </Badge>
          ) : (
            <Badge variant="outline">Permanent</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 p-4 pt-0">
        {detail.is_temporary && (
          <div className="space-y-2">
            <QuotaBar label="input" used={detail.input_tokens_used} limit={detail.temp_input_token_limit} />
            <QuotaBar label="output" used={detail.output_tokens_used} limit={detail.temp_output_token_limit} />
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Leave a field blank to not enforce that limit. The token quota is two caps — access is
          blocked when either input or output is reached. Days count from first login; hours and
          minutes set a deadline from now.
        </p>
        <div className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Expire after</span>
          <div className="flex gap-2">
            <Input
              type="number"
              min={1}
              className="flex-1"
              value={expireValue}
              onChange={(e) => setExpireValue(e.target.value)}
              placeholder="not enforced"
            />
            <Select value={expireUnit} onValueChange={(v) => setExpireUnit(v as ExpiryUnit)}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minutes">Minutes</SelectItem>
                <SelectItem value="hours">Hours</SelectItem>
                <SelectItem value="days">Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Input token limit</span>
          <Input
            type="number"
            min={1}
            value={inputTokens}
            onChange={(e) => setInputTokens(e.target.value)}
            placeholder="not enforced"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Output token limit</span>
          <Input
            type="number"
            min={1}
            value={outputTokens}
            onChange={(e) => setOutputTokens(e.target.value)}
            placeholder="not enforced"
          />
        </label>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={busy} onClick={() => save(true)}>
            {detail.is_temporary ? 'Save limits' : 'Make temporary'}
          </Button>
          {detail.is_temporary && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => save(false)}>
              Convert to permanent
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
